import React, { useEffect, useState } from 'react'
import {
  CCloseButton,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CPlaceholder,
} from '@coreui/react-pro'
import AnalyticsV2Service from 'src/service/AnalyticsV2Service'
import { EmptyState, ErrorState, formatDecimal, formatNumber } from './common'
import { useChartTheme } from './chartTheme'

/**
 * One side of the flow: a ranked list with a proportional bar behind each row.
 * A bar chart would need its own axis for ten short lists; the inline bar reads
 * faster and keeps the exact number beside it.
 */
const FlowList = ({ title, hint, rows, emptyLabel, theme }) => {
  const max = rows.reduce((m, row) => Math.max(m, Number(row.count) || 0), 0)

  return (
    <div className="mb-4">
      <div className="fw-semibold">{title}</div>
      <div className="small text-body-secondary mb-2">{hint}</div>

      {rows.length === 0 && <div className="small text-body-secondary py-3">{emptyLabel}</div>}

      {rows.map((row) => (
        <div key={row.page_name} className="av2-flow-row">
          <div
            className="av2-flow-fill"
            style={{
              width: `${max > 0 ? (Number(row.count) / max) * 100 : 0}%`,
              background: theme.series[0],
            }}
            aria-hidden="true"
          />
          <div className="av2-flow-text">
            <span className="av2-flow-name" title={row.page_name}>
              {row.page_name}
            </span>
            <span className="av2-flow-value">
              {formatNumber(row.count)}
              <span className="text-body-secondary ms-2">{formatDecimal(row.share_pct, 1)}%</span>
            </span>
          </div>
        </div>
      ))}
    </div>
  )
}

const ScreenFlowOffcanvas = ({ visible, pageName, filters, onClose }) => {
  const theme = useChartTheme()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!visible || !pageName) return

    let cancelled = false
    setLoading(true)
    setError(null)

    AnalyticsV2Service.getScreenFlow(filters, pageName)
      .then((res) => {
        if (cancelled) return
        if (res.success) {
          setData(res.data)
        } else {
          setError(res.message)
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Request failed.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [visible, pageName, filters])

  const inbound = data?.inbound || []
  const outbound = data?.outbound || []
  const hasAny = inbound.length > 0 || outbound.length > 0

  return (
    <COffcanvas placement="end" visible={visible} onHide={onClose} className="av2-offcanvas">
      <COffcanvasHeader>
        <div>
          <COffcanvasTitle>{pageName}</COffcanvasTitle>
          <div className="small text-body-secondary">How people reach this screen and leave it</div>
        </div>
        <CCloseButton className="text-reset" onClick={onClose} />
      </COffcanvasHeader>

      <COffcanvasBody>
        {loading && (
          <CPlaceholder component="div" animation="glow">
            {[8, 6, 9, 5, 7, 4].map((w, i) => (
              <CPlaceholder key={i} xs={w} className="mb-2 d-block" />
            ))}
          </CPlaceholder>
        )}

        {!loading && error && <ErrorState message={error} height={200} />}

        {!loading && !error && !hasAny && (
          <EmptyState
            title="No journeys recorded for this screen"
            hint="This screen had views in the period, but no recorded previous or next screen."
            height={200}
          />
        )}

        {!loading && !error && hasAny && (
          <>
            <FlowList
              title="Came from"
              hint="The screen people were on immediately before this one"
              rows={inbound}
              emptyLabel="No inbound screens recorded."
              theme={theme}
            />
            <FlowList
              title="Went to next"
              hint="Where people went immediately after this screen"
              rows={outbound}
              emptyLabel="No onward screens recorded."
              theme={theme}
            />
            <div className="small text-body-secondary">
              Top 10 each, by number of screen views in the selected period.
            </div>
          </>
        )}
      </COffcanvasBody>
    </COffcanvas>
  )
}

export default ScreenFlowOffcanvas
